import toPath from 'lodash/toPath.js'
import cn from '../../../common/className.js'
import EnableLabelProperty, {PickLabelProperty} from './EnableLabelProperty.js'
import {
  isLabelPropertyEnabledSelector,
  labelConfigSelector,
  setLabelInfoPath,
} from '../../../data/labelInfos/index.js'
import useLabelProperty, {formatMetaClassName} from './useLabelProperty.js'
import {useSelector} from '../../../store.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'
import Icon from '../../../common/icons/Icon.js'

export function onCheckboxLabelPropertyEnable({labelInfoID}, labelProperty) {
  setLabelInfoPath(labelInfoID, ['config', ...toPath(labelProperty)], true)
}

/**
 * A label config checkbox
 *
 * @param {Object} props
 * @param {String} [props.className]
 * @param {String} [props.id]
 * @param {Boolean} [props.checked]
 * @param {Boolean} [props.disabled]
 * @param {Boolean} [props.hide]
 * @param {Function} [props.Label]
 * @param {String} [props.label]
 * @param {Function} props.onChange
 * @param {String} [props.labelProperty]
 * @param {Function} [props.labelPropertyCheckedSelector]
 * @param {Object} [props.propertySpecs]
 * @param {Function} props.propertySpecs.labelPropertiesFunc
 * @param {Array} props.propertySpecs.validShipperTypes
 * @param {Function} props.propertySpecs.dependentPropertiesFunc
 * @param {Function} props.propertySpecs.validLabelPropertiesFunc
 * @param {Function} props.propertySpecs.onLabelPropertyEnable
 * @param {String} [props.helpLink]
 * @param {String} [props.helpLinkHint]
 * @returns {React.ReactNode}
 */
export default function ConfigCheckbox({
  className,
  id,
  checked,
  disabled,
  hide,
  Label,
  label,
  onChange,
  labelProperty,
  labelPropertyCheckedSelector,
  propertySpecs,
  helpLink,
  helpLinkHint,
}) {
  const {labelInfoID, shipperType, isLabelEnableMode, isSummary, isPickMode} =
    useLabelConfigContext()

  labelProperty = labelProperty || ''
  id = id || `${labelInfoID}__${labelProperty}`
  propertySpecs.onLabelPropertyEnable ||= onCheckboxLabelPropertyEnable

  useLabelProperty({
    labelProperty,
    propertySpecs,
  })

  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty,
    }),
  )

  checked = useSelector((state) =>
    checked === undefined
      ? labelConfigSelector(state, {labelInfoID})[labelProperty] || false
      : checked,
  )

  hide ||=
    !!shipperType && !propertySpecs.validShipperTypes.includes(shipperType)

  disabled ||= isLabelEnableMode && !isLabelPropertyEnabled

  if (hide) {
    return null
  }

  if (isSummary) {
    return label
  }

  if (isPickMode && !isLabelPropertyEnabled) {
    return (
      <PickLabelProperty
        label={label}
        labelProperty={labelProperty}
        checkedSelector={labelPropertyCheckedSelector}
      />
    )
  }

  return (
    <li
      className={cn`flex-item list__item--shipping-options ${{
        'property-disabled': disabled,
      }}`}
    >
      <div className="wrap--edit-preset-form-input">
        <label
          className={cn`margin-bottom-0 ${formatMetaClassName(
            labelProperty,
          )} ${className}`}
          htmlFor={id}
        >
          <input
            type="checkbox"
            className="v-align-middle margin-bottom-0 margin-right-5"
            id={id}
            checked={checked}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked)}
          />
          {Label ? (
            <span className="label--config__txt">
              <Label />
            </span>
          ) : (
            <span className="label--config__txt v-align-middle">{label}</span>
          )}
          {helpLink && (
            <a
              className="btn btn--link no-underline tooltip--top"
              data-hint={helpLinkHint}
              href={helpLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="sym-support" className="fs-n1 margin-left-5" />
            </a>
          )}
        </label>
      </div>
      <EnableLabelProperty
        labelProperty={labelProperty}
        checkedSelector={labelPropertyCheckedSelector}
      />
    </li>
  )
}
